import type { JSX, ParentProps } from 'solid-js'
import { css } from '@emotion/css';
import { Mode } from 'Shared/posters/project/PosterMode';
import type { SelectionContext } from 'Shared/posters/canvas/selection';
import type { CanvasContext } from 'Shared/posters/canvas/canvas';
import type { DragContext } from 'Shared/posters/canvas/drag';
import type { HistoryContext } from 'Shared/posters/project/history';
import { Show } from 'solid-js';
import { anyTemplateContentCropped } from './text/BaseTextObject';
import type { ObjectContext } from 'Shared/posters/project/objects';
import type { ImageContext } from 'Shared/posters/canvas/images/images';
import type { UploadContext } from 'Shared/posters/canvas/images/upload';
import { DataRecord } from 'Shared/posters/types';
import { ThemeRecord } from 'Shared/posters/project/themeVariables';
import { ISave } from 'Shared/posters/project/PosterRenderer';
import type { DesignLinkContext } from 'Shared/posters/project/designLink';

interface ICanvasWrap {
	children:   JSX.Element;
	record: DataRecord;
	theme: ThemeRecord;
	mode: Mode;
	objects: ObjectContext;
}

export interface ICanvasPane {
	textRefs: Record<string, SVGTextElement>;
	/** The event record whose field data populates the poster's field objects. */
	record: DataRecord;
	/** Resolved theme variables, threaded alongside the record for bound properties/fields. */
	theme: ThemeRecord | undefined;
	/** Optional wrapper component placed around the rendered SVG (defaults to BasicPosterWrap). */
	wrap?: (props: ParentProps) => JSX.Element;
	mode: Mode;
	objects: ObjectContext;
	selection: SelectionContext;
	canvas: CanvasContext;
	drag: DragContext;
	history: HistoryContext;
	images: ImageContext;
	/** User-mode link to the nested template design; inert (linked() === false) otherwise. */
	design: DesignLinkContext;
	/** Present only in editable modes; the read-only poster never uploads. */
	upload?: UploadContext;

	posterId?: string;
	save: ISave;
}

export function CanvasWrap(P: ICanvasWrap)
{
	const objCtx = P.objects;

	const canvasStyle = css({
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-start'
	});

	const croppedBadgeStyle = css({
		position: 'absolute',
		left: '100%',
		top: 0,
		marginLeft: 12,
		width: 32,
		height: 32,
		borderRadius: '50%',
		background: '#4d9eff',
		color: '#fff',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: 'Georgia, "Times New Roman", serif',
		fontStyle: 'italic',
		fontWeight: 'bold',
		fontSize: 20,
		lineHeight: 1,
		cursor: 'help',
		userSelect: 'none',
		boxShadow: '0 2px 6px rgba(0,0,0,.4)',
	});

	// An unselected event has no record (eventData defaults to {}), so field templates resolve
	// against nothing — the hint would flag "overflow" against placeholder text with no record to preview.
	const anyCropped = () => !!P.record && Object.keys(P.record).length>0 && P.mode!='user'
		&& anyTemplateContentCropped(objCtx.objects(), P.record, P.theme);

	return (
		<div class={canvasStyle}>
			 <div style={{ position: 'relative', display: 'inline-block' }}>
				<Show when={anyCropped()}>
					<div class={croppedBadgeStyle} title='See the user preview for an accurate display of this poster.'>i</div>
				</Show>

				{P.children}
			</div>
		</div>
	)
}

